import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  deleteUser,
  listUsers,
  resetUserPassword,
  type TenantUser,
} from '../api/users.js';
import { ApiError } from '../api/apiClient.js';
import { ConfirmModal } from '../components/ConfirmModal.js';
import { useUiStore } from '../state/uiStore.js';
import { t } from '../i18n/es.js';

export function UsersList(): JSX.Element {
  const { id = '' } = useParams<{ id: string }>();
  const [confirmTarget, setConfirmTarget] = useState<TenantUser | null>(null);
  const pushToast = useUiStore((s) => s.pushToast);
  const queryClient = useQueryClient();

  const { data, isLoading, error } = useQuery({
    queryKey: ['users', id],
    queryFn: () => listUsers(id),
    enabled: Boolean(id),
  });

  const deleteMutation = useMutation({
    mutationFn: (username: string) => deleteUser(id, username),
    onSuccess: () => {
      pushToast('success', t.user.deleted);
      queryClient.invalidateQueries({ queryKey: ['users', id] });
      setConfirmTarget(null);
    },
    onError: (e: unknown) => {
      const msg = e instanceof ApiError ? e.message : (e as Error).message;
      pushToast('error', msg);
    },
  });

  const resetMutation = useMutation({
    mutationFn: (username: string) => resetUserPassword(id, username),
    onSuccess: () => pushToast('success', t.user.passwordReset),
    onError: (e: unknown) => {
      const msg = e instanceof ApiError ? e.message : (e as Error).message;
      pushToast('error', msg);
    },
  });

  const users = data ?? [];

  return (
    <div>
      <div className="mb-4 flex items-center justify-between">
        <h2 className="text-xl font-semibold">{t.user.listTitle}</h2>
        <Link
          to={`/tenants/${id}/users/new`}
          className="rounded bg-brand-500 px-3 py-1 text-sm text-white hover:bg-brand-600"
        >
          {t.user.newBtn}
        </Link>
      </div>

      {isLoading && <p className="text-slate-500">{t.app.loading}</p>}
      {error && <p className="text-red-700">{(error as Error).message}</p>}
      {!isLoading && !error && users.length === 0 && (
        <p className="text-slate-500">{t.user.empty}</p>
      )}

      {users.length > 0 && (
        <div className="overflow-x-auto rounded border border-slate-200 bg-white">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left text-xs uppercase text-slate-500">
              <tr>
                <th className="px-4 py-2">{t.user.fields.email}</th>
                <th className="px-4 py-2">{t.user.fields.displayName}</th>
                <th className="px-4 py-2">{t.user.fields.roles}</th>
                <th className="px-4 py-2">{t.user.fields.status}</th>
                <th className="px-4 py-2 text-right">{t.user.fields.actions}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {users.map((u) => (
                <tr key={u.username} data-testid={`user-row-${u.username}`}>
                  <td className="px-4 py-2 font-mono text-xs">{u.email}</td>
                  <td className="px-4 py-2">{u.displayName ?? '—'}</td>
                  <td className="px-4 py-2">
                    <div className="flex flex-wrap gap-1">
                      {u.roles.map((r) => (
                        <span
                          key={r}
                          className={`rounded px-2 py-0.5 text-xs ${
                            r === 'admin'
                              ? 'bg-brand-100 text-brand-800'
                              : r === 'platform-ops'
                                ? 'bg-amber-100 text-amber-900'
                                : 'bg-slate-100 text-slate-700'
                          }`}
                        >
                          {r}
                        </span>
                      ))}
                    </div>
                  </td>
                  <td className="px-4 py-2 text-xs text-slate-600">{u.status ?? '—'}</td>
                  <td className="px-4 py-2 text-right">
                    <div className="flex justify-end gap-2">
                      <button
                        type="button"
                        onClick={() => resetMutation.mutate(u.username)}
                        disabled={resetMutation.isPending}
                        className="rounded border border-slate-300 px-2 py-1 text-xs hover:bg-slate-50"
                      >
                        {t.user.resetPassword}
                      </button>
                      <Link
                        to={`/tenants/${id}/users/${encodeURIComponent(u.username)}/edit`}
                        className="rounded border border-slate-300 px-2 py-1 text-xs hover:bg-slate-50"
                      >
                        {t.user.edit}
                      </Link>
                      <button
                        type="button"
                        onClick={() => setConfirmTarget(u)}
                        className="rounded border border-red-300 px-2 py-1 text-xs text-red-700 hover:bg-red-50"
                      >
                        {t.user.delete}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <ConfirmModal
        open={confirmTarget !== null}
        title={t.user.confirmDeleteTitle}
        description={
          confirmTarget
            ? t.user.confirmDeleteDesc.replace('{{email}}', confirmTarget.email)
            : ''
        }
        confirmLabel={t.user.delete}
        cancelLabel={t.app.cancel}
        loading={deleteMutation.isPending}
        onConfirm={() => {
          if (confirmTarget) deleteMutation.mutate(confirmTarget.username);
        }}
        onCancel={() => setConfirmTarget(null)}
      />
    </div>
  );
}
